import { ref, watch } from 'vue';
import { showPhoneValidationForm } from '../utils/phoneValidation';
import { showNameForm } from '../utils/nameValidation';
import { showIdVerificationForm } from '../utils/IdVerification';
import { deliveryteamOrdersApiClient } from '@api-client/deliveryteam_orders';
import type { COURIER_MODES_ARRAY } from '@lib/zod/CourierSchemas';
import { startRefreshingTokens } from "@api-client/auth/auth";
import { authState } from "@/ui-plus/useAuth";
import { toast } from '@/ui-plus/sonner';

type CourierMode = (typeof COURIER_MODES_ARRAY)[number];

export const courierMode = ref<CourierMode>('WALKING');
export const canCarrySmallPackage = ref(true);
export const canCarryPizzaBox = ref(false);
export const canCarryMediumPackage = ref(false);
export const canCarryLargePackageCarTrunkRequired = ref(false);

export const isCourierProfileLoading = ref(false);
export const isCourierProfileSaving = ref(false);
export const courierProfileError = ref<string | null>(null);
export const courierOrderAlertPhoneNumbers = ref<string[]>([]);

let isHydratingFromApi = false;

export async function saveCourierProfile() {

    isCourierProfileSaving.value = true;
    courierProfileError.value = null;

    const response = await (await deliveryteamOrdersApiClient.courier_update_profile.$post({
        json: {
            mode: courierMode.value,
            canCarrySmallPackage: canCarrySmallPackage.value,
            canCarryPizzaBox: canCarryPizzaBox.value,
            canCarryMediumPackage: canCarryMediumPackage.value,
            canCarryLargePackageCarTrunkRequired: canCarryLargePackageCarTrunkRequired.value,
            courierOrderAlertPhoneNumbers: courierOrderAlertPhoneNumbers.value,
        },
    })).json();

    if (response.error) {
        courierProfileError.value = 'Failed to save courier profile';
    }

    isCourierProfileSaving.value = false;
}

export async function loadCourierProfile() {
    if (!authState.value.accessTokenPayload?.hasVerifiedPhoneNumber) {
        const result = await showPhoneValidationForm();
        if (result === 1) {
            toast.success('Phone number verified, reloading profile...');
            await startRefreshingTokens();
        } else {
            toast.error('Phone number verification failed, please try again');
            // wait 2 seconds and then try again
            await startRefreshingTokens();
            loadCourierProfile();
            return;
        }
    }

    if (!authState.value.accessTokenPayload?.name) {
        const result = await showNameForm();
        if (result === 1) {
            toast.success('Name saved, reloading profile...');
            await startRefreshingTokens();
        } else {
            toast.error('Name is required, please try again');
            await startRefreshingTokens();
            loadCourierProfile();
            return;
        }
    }

    if (!authState.value.accessTokenPayload?.hasVerifiedId) {
        const result = await showIdVerificationForm();
        if (result === 1) {
            toast.success('ID verification successful, reloading profile...');
            await startRefreshingTokens();
        } else {
            toast.error('ID verification failed, please try again');
            // wait 2 seconds and then try again
            await startRefreshingTokens();
            loadCourierProfile();
            return;
        }
    }


    isCourierProfileLoading.value = true;
    courierProfileError.value = null;


    const response = await (await deliveryteamOrdersApiClient.courier_get_profile.$post({
    })).json();

    if (response.error || !response.data) {
        if (
            response.error?.code === 'USER_PHONE_NUMBER_NOT_VERIFIED' ||
            response.error?.code === 'USER_ID_NOT_VERIFIED'
        ) {
            await startRefreshingTokens();
            loadCourierProfile();
            return;
        } else {
            courierProfileError.value = 'Failed to load courier profile';
            isCourierProfileLoading.value = false;
            return;
        }
    }

    isHydratingFromApi = true;
    courierMode.value = response.data.mode;
    canCarrySmallPackage.value = response.data.canCarrySmallPackage;
    canCarryPizzaBox.value = response.data.canCarryPizzaBox;
    canCarryMediumPackage.value = response.data.canCarryMediumPackage;
    canCarryLargePackageCarTrunkRequired.value = response.data.canCarryLargePackageCarTrunkRequired;
    courierOrderAlertPhoneNumbers.value = response.data.courierOrderAlertPhoneNumbers ?? [];
    isHydratingFromApi = false;

    isCourierProfileLoading.value = false;
}


let saveTimer: ReturnType<typeof setTimeout> | null = null;
export function clearSaveTimer() {
    if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
    }
}
watch(
    [
        courierMode,
        canCarrySmallPackage,
        canCarryPizzaBox,
        canCarryMediumPackage,
        canCarryLargePackageCarTrunkRequired,
        courierOrderAlertPhoneNumbers,
    ],
    () => {
        if (isHydratingFromApi || isCourierProfileLoading.value) return;

        clearSaveTimer();
        saveTimer = setTimeout(() => {
            void saveCourierProfile();
        }, 700);
    },
    { immediate: false }
);
